import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import LoginPage from "./components/LoginPage";
import Dashboard from "./components/Dashboard";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import InstallPrompt from "./components/InstallPrompt";
import SubscriptionExpired from "./components/SubscriptionExpired";
import PrivacyPolicy from "./components/PrivacyPolicy";
import OfflineIndicator from "./components/OfflineIndicator";
import MinisterVerification from "./components/MinisterVerification";
import LiturgicalNotifications from "./components/LiturgicalNotifications";
import { installHtmlErrorScrubber, installTransientRetry } from "./utils/apiError";

// Global safeguard (2026-02-21): scrub Cloudflare / origin HTML error
// pages from every axios response BEFORE any downstream .catch runs.
// Installed at module scope so it fires even for requests made during
// the first render (e.g. auth /me), which is what leaked the raw
// "The origin web server sent a response…" text into the login form,
// lesson content page, and Deep Dive panel.
installTransientRetry(axios);
installHtmlErrorScrubber(axios);
import { Toaster } from "./components/ui/sonner";
import { CelestialProvider } from "./contexts/CelestialContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── 2026-02-11d LOADING SAFEGUARD ─────────────────────────────────
// Hard ceiling on how long any single request can stall the app.
// Without this, a hung backend (cold lambda, DNS blip, mobile
// captive portal) leaves users staring at the gold "Loading…"
// screen forever — the exact production bug screenshotted by the
// user on ylcenter.com. Axios defaults to "no timeout" which is
// the wrong default for a learner-facing PWA.
//
// 2026-02-16 ADJUSTED: original 12s timeout was tripping on
// production cold-start of `/api/auth/login` (Mongo + bcrypt
// can take 15-20s on first request after idle), producing
// the "An error occurred. Please try again." login error.
// 30s is long enough for cold-start while still preventing
// indefinite hang.
axios.defaults.timeout = 30000; // 30s — tolerates backend cold starts

// ── 2026-02-21 Global axios response interceptor ─────────────────
// User report: "An error occurred. Please try again." on ylcenter.com
// login. Root cause: platform-level TLS handshake failure on the OLD
// custom domain, so axios threw a network error with NO `response`
// object and every local `catch` fell through to the generic string.
//
// This interceptor gives every existing `err.response?.data?.detail`
// reader in the app (there are ~38 of them) a clean, readable string
// in place of the raw payload. Specifically:
//   • Rewrites FastAPI 422 detail arrays → "; "-joined msg strings.
//     This prevents "Objects are not valid as a React child" (#31)
//     across the app when a `catch` handler renders `detail` directly.
//   • Synthesizes a `.friendlyMessage` field on every axios error so
//     new call sites can just show `err.friendlyMessage` if they want
//     an already-humanized string, without another try/catch layer.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const raw = err?.response?.data?.detail;
      if (Array.isArray(raw)) {
        err.response.data.detail = raw
          .map((d) => (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d)))
          .filter(Boolean)
          .join("; ");
      }
      // Attach a pre-baked human-readable message for downstream use.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.trim()) {
        err.friendlyMessage = detail;
      } else if (err?.code === "ECONNABORTED") {
        err.friendlyMessage = "The request timed out. Please check your connection and try again.";
      } else if (!err?.response) {
        err.friendlyMessage = "We can't reach the server right now. Please check your internet connection or try again in a moment.";
      } else if (status >= 500) {
        err.friendlyMessage = "Server error — please try again shortly.";
      } else if (status === 429) {
        err.friendlyMessage = "Too many requests. Please wait a moment and try again.";
      } else {
        err.friendlyMessage = "Something went wrong. Please try again.";
      }
    } catch {
      /* interceptor must never throw — swallow */
    }
    return Promise.reject(err);
  }
);

// Inactivity timeout in milliseconds (15 minutes)
const INACTIVITY_TIMEOUT = 15 * 60 * 1000;
// Warning before logout (1 minute before)
const WARNING_BEFORE_LOGOUT = 60 * 1000;

// Maximum time the initial "Loading…" splash is allowed to persist
// before we force-dismiss it and let the user reach the login page
// (or the offline indicator). This is a belt-and-braces safeguard
// in addition to the axios timeout above.
const BOOT_SPLASH_MAX_MS = 10000;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showMinisterVerification, setShowMinisterVerification] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Check URL for privacy policy and minister verification routes
  useEffect(() => {
    const path = window.location.pathname;
    if (path === "/privacy" || path === "/privacy-policy") {
      setShowPrivacyPolicy(true);
    } else if (path === "/verify-minister" || path === "/minister-verification") {
      setShowMinisterVerification(true);
    }
  }, []);

  // Global axios interceptor for subscription enforcement, rate
  // limiting, AND session-expiry ("kicks user to login" bug):
  // instead of letting every downstream component render a broken
  // state when its own request 401s, we scrub the stale token here,
  // fall the app back to the LoginPage gracefully, and — critically
  // — do NOT hijack the /auth/login response itself (a wrong-password
  // 401 must stay a form error, not a boot-flow reset).
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url || "";
        const isAuthAttempt =
          url.includes("/auth/login") ||
          url.includes("/auth/register") ||
          url.includes("/auth/google/session");
        if (status === 401 && !isAuthAttempt) {
          // Session expired mid-flight → hard log out but keep the
          // user oriented with a one-line reason instead of a silent
          // kick to the login page.
          if (localStorage.getItem("token")) {
            localStorage.removeItem("token");
            setUser(null);
            setSubscriptionExpired(false);
            setShowInactivityWarning(false);
            // eslint-disable-next-line no-alert
            alert("Your session has expired — please sign in again.");
          }
        } else if (status === 402) {
          setSubscriptionExpired(true);
          fetchSubscriptionStatus();
        } else if (status === 429) {
          alert(error.response?.data?.detail || "Too many requests. Please try again later.");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await axios.get(`${API}/auth/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionStatus(response.data);
      }
    } catch (error) {
      console.error("Error fetching subscription status:", error);
    }
  };

  // Detect the Emergent Google Auth redirect BEFORE we do any other
  // /auth/me checks so its /session exchange fires first.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH.
  const hasGoogleCallback =
    typeof window !== "undefined" &&
    (window.location.hash || "").includes("session_id=");

  useEffect(() => {
    // If we're mid-Google-callback, skip the localStorage /me check —
    // the GoogleAuthCallback component will call handleLogin itself.
    if (hasGoogleCallback) {
      setLoading(false);
      return;
    }
    // Check if user is already logged in
    const token = localStorage.getItem("token");
    if (token) {
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }
    
    // Fetch config
    fetchConfig();

    // 2026-02-11d watchdog: if for any reason `loading` is still
    // true after BOOT_SPLASH_MAX_MS (network dead, axios stuck,
    // CDN delivering stale bundle, etc.) force-dismiss the splash
    // so the user reaches the login screen instead of staring at
    // "Loading…" indefinitely. This was the production-only bug
    // reported by the user (ylcenter.com stuck on Loading…).
    const bootWatchdog = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          // eslint-disable-next-line no-console
          console.warn(
            `[YLC] Boot watchdog fired after ${BOOT_SPLASH_MAX_MS}ms — ` +
              `forcing splash dismissal so the UI is not stuck on "Loading…".`
          );
        }
        return false;
      });
    }, BOOT_SPLASH_MAX_MS);

    // Check URL params for subscription success/cancel
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionResult = urlParams.get("subscription");
    if (subscriptionResult === "success") {
      alert("🎉 Subscription activated successfully! Welcome to Yisrael Learning Center.");
      window.history.replaceState({}, document.title, window.location.pathname);
      setSubscriptionExpired(false);
      // Refresh user data to get updated subscription status
      const currentToken = localStorage.getItem("token");
      if (currentToken) {
        fetchCurrentUser(currentToken);
      }
    } else if (subscriptionResult === "cancelled") {
      alert("Subscription checkout was cancelled. You can subscribe anytime from the Subscription page.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return () => clearTimeout(bootWatchdog);
  }, []);

  // Logout function
  const handleLogout = useCallback((reason = "manual") => {
    localStorage.removeItem("token");
    setUser(null);
    setSubscriptionExpired(false);
    setSubscriptionStatus(null);
    setShowInactivityWarning(false);
    if (reason === "inactivity") {
      alert("You have been logged out due to inactivity. Please log in again.");
    }
  }, []);

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    setLastActivity(Date.now());
    setShowInactivityWarning(false);
  }, []);

  // Auto-logout for inactivity
  useEffect(() => {
    if (!user) return; // Only track activity when logged in

    // Activity events to track
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttled activity handler (only update every 30 seconds to avoid performance issues)
    let lastUpdate = Date.now();
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > 30000) { // Update every 30 seconds of activity
        lastUpdate = now;
        resetActivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Check inactivity periodically
    const checkInactivity = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      // Show warning 1 minute before logout
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT && 
          timeSinceLastActivity < INACTIVITY_TIMEOUT) {
        setShowInactivityWarning(true);
      }

      // Auto logout after inactivity timeout
      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        handleLogout("inactivity");
      }
    }, 10000); // Check every 10 seconds

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearInterval(checkInactivity);
    };
  }, [user, lastActivity, handleLogout, resetActivityTimer]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  const fetchCurrentUser = async (token) => {
    try {
      // First check subscription status (doesn't require active subscription)
      const statusResponse = await axios.get(`${API}/auth/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(statusResponse.data);
      
      // If user has access, get full user data
      if (statusResponse.data.has_access) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setSubscriptionExpired(false);
      } else {
        // Trial expired or inactive - show subscription page
        setUser({
          id: statusResponse.data.user_id,
          username: statusResponse.data.username,
          subscription_status: statusResponse.data.subscription_status,
          trial_days_remaining: statusResponse.data.trial_days_remaining
        });
        setSubscriptionExpired(true);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      
      if (error.response?.status === 401) {
        // Token invalid - logout
        localStorage.removeItem("token");
        setUser(null);
      } else if (error.response?.status === 402) {
        // Subscription expired
        setSubscriptionExpired(true);
        fetchSubscriptionStatus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem("token", token);
    setUser(userData);
    // Notify CelestialContext (and any other auth-aware listeners) that
    // a token is now available — they can refetch immediately instead
    // of waiting for the next 60-second poll.
    try {
      window.dispatchEvent(new CustomEvent("ylc:auth-changed", { detail: { authenticated: true } }));
    } catch {
      /* non-fatal */
    }
    
    // Check if user has access (trial active or subscribed)
    const status = userData.subscription_status;
    const trialDays = userData.trial_days_remaining;
    
    if (status === "active") {
      setSubscriptionExpired(false);
    } else if (status === "trial" && trialDays > 0) {
      setSubscriptionExpired(false);
    } else {
      setSubscriptionExpired(true);
      fetchSubscriptionStatus();
    }
  };

  // Show Privacy Policy (accessible without login)
  if (showPrivacyPolicy) {
    return (
      <>
        <OfflineIndicator />
        <PrivacyPolicy 
          config={config} 
          onBack={() => {
            setShowPrivacyPolicy(false);
            window.history.pushState({}, "", "/");
          }} 
        />
      </>
    );
  }

  // Show Minister Verification (public page)
  if (showMinisterVerification) {
    return (
      <>
        <OfflineIndicator />
        <MinisterVerification />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#111]">
        <div className="text-[#FFD700] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <CelestialProvider>
    <div className="App">
      <OfflineIndicator />
      <InstallPrompt />
      {/* Global sonner toaster — required so LiturgicalNotifications
          (sundown / preparation day / Sabbath day) can surface
          biblical-calendar transitions anywhere in the app. */}
      <Toaster position="top-center" richColors closeButton theme="dark" />
      {/* Background biblical-calendar event watcher. Mounted only
          when the user is authenticated so we don't poll for
          anonymous visitors. */}
      {user && <LiturgicalNotifications user={user} />}
      
      {/* Inactivity Warning Modal */}
      {showInactivityWarning && user && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000
          }}
          data-testid="inactivity-warning-modal"
        >
          <div style={{
            background: "#1a1a1a",
            padding: "30px",
            borderRadius: "12px",
            border: "2px solid #FFD700",
            maxWidth: "400px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "15px" }}>⏰</div>
            <h2 style={{ color: "#FFD700", marginBottom: "15px" }}>Session Timeout Warning</h2>
            <p style={{ color: "#CCCCCC", marginBottom: "20px" }}>
              You will be logged out in less than 1 minute due to inactivity.
            </p>
            <button
              onClick={resetActivityTimer}
              style={{
                background: "#FFD700",
                color: "#000",
                padding: "12px 30px",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: "bold",
                cursor: "pointer",
                marginTop: 0
              }}
              data-testid="stay-logged-in-btn"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
      
      {hasGoogleCallback && !user ? (
        <GoogleAuthCallback
          onLogin={handleLogin}
          onError={(msg) => {
            // eslint-disable-next-line no-console
            console.error("[YLC] Google auth error:", msg);
            setLoading(false);
          }}
        />
      ) : !user ? (
        <LoginPage 
          onLogin={handleLogin} 
          config={config} 
          onShowPrivacy={() => {
            setShowPrivacyPolicy(true);
            window.history.pushState({}, "", "/privacy");
          }}
        />
      ) : subscriptionExpired ? (
        <SubscriptionExpired 
          config={config} 
          onLogout={handleLogout} 
          subscriptionStatus={subscriptionStatus}
          user={user}
          onShowPrivacy={() => {
            setShowPrivacyPolicy(true);
            window.history.pushState({}, "", "/privacy");
          }}
        />
      ) : (
        <Dashboard 
          user={user} 
          onLogout={handleLogout} 
          config={config}
          onShowPrivacy={() => {
            setShowPrivacyPolicy(true);
            window.history.pushState({}, "", "/privacy");
          }}
        />
      )}
    </div>
    </CelestialProvider>
  );
}

export default App;